import { forwardRef, type AnchorHTMLAttributes, type ButtonHTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

export const linkVariants = cva(
  "cursor-pointer transition-colors duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tb-green focus-visible:ring-offset-2",
  {
    variants: {
      variant: {
        /** Standard inline link — underline, body color, green on hover. */
        inline:
          "border-b border-foreground pb-px text-[15px] text-foreground hover:border-tb-green hover:text-tb-green",
        /** Subtle action link — small, underlined, muted. For escape hatches. */
        muted:
          "text-[12px] tracking-[0.06em] text-fg-muted underline underline-offset-[3px] hover:text-primary",
      },
    },
    defaultVariants: { variant: "inline" },
  },
);

export interface LinkProps
  extends AnchorHTMLAttributes<HTMLAnchorElement>,
    VariantProps<typeof linkVariants> {}

/**
 * Styled anchor link, from the TB Design System.
 *
 * For links that navigate; `LinkButton` is the one for actions that look like links but trigger
 * callbacks (e.g. "Não é você? Usar outro CPF").
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ className, variant, ...props }, ref) => (
    <a ref={ref} className={cn(linkVariants({ variant, className }))} {...props} />
  ),
);
Link.displayName = "Link";

export interface LinkButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof linkVariants> {}

/**
 * Button styled as a link — same variants as `Link`, but renders a `<button>`.
 *
 * For actions that look like a link and do not navigate; the semantics stay a button.
 */
export const LinkButton = forwardRef<HTMLButtonElement, LinkButtonProps>(
  ({ className, variant, type = "button", ...props }, ref) => (
    <button ref={ref} type={type} className={cn(linkVariants({ variant, className }))} {...props} />
  ),
);
LinkButton.displayName = "LinkButton";
